import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import SearchSelect from "@/layouts/client/services/service_utilize/components/search_select/SearchSelect.vue";
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { validate_south_african_id, validate_company_registration, allow_character, validate_date_of_birth_with_slashes } from '@/utils/validations.js';

export default {
  name: 'CustomService',
  mixins: [MAIN_CONTROLLER],
  components: {
    BreadCrumb,
    ClientLogo,
    SearchSelect
  },
  data() {
    return {
      is_form_data_ready: false,
      form_data: [],
      responses: [],
      file_input: null,
      follow_up_file_input: null,
      uploaded_file: null,
      follow_up_responses: [],
      follow_up_questions: [],
      duplicate_fields: [], // Stores dynamic sets of duplicated fields
      validation_errors: {}, // To store validation error messages dynamically
      service_type: "",
      service_name: "",
      is_incomplete: false
    };
  },
  computed: {
    service_id() {
      return this.$store.getters.get_service_id;
    },
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    }
  },

  watch: {
    service_id: {
      handler(new_value) {
        if (new_value) {
          this.fetch_custom_service_question();
        }
      },
      immediate: true,
    },
  },
  methods: {
    validate_field_by_question(field) {
      // Only apply specific validations for Entity Data Capture service and CIPC services
      const isCIPCService = this.service_name && this.service_name.toLowerCase().includes('cipc');
      const isEntityDataCapture = this.service_name === 'Entity Data Capture';
      
      if (!isEntityDataCapture && !isCIPCService) {
        return { isValid: true, message: '' };
      }

      const question_text = field.question ? field.question.trim() : '';
      const value = field.value;

      // Validate ID Number (Entity Data Capture only)
      if (isEntityDataCapture && question_text === 'ID Number' && value) {
        const result = validate_south_african_id(value);
        if (!result.isValid) {
          return { isValid: false, message: result.message };
        }
      }

      // Validate Company Registration Number (Both Entity Data Capture and CIPC)
      if (question_text === 'Company Registration Number' && value) {
        const result = validate_company_registration(value);
        if (!result.isValid) {
          return { isValid: false, message: result.message };
        }
        // Update field value with formatted version if validation passed
        if (result.value && result.value !== value) {
          field.value = result.value;
        }
      }

      // Validate First Name and Last Name - no numbers allowed (Entity Data Capture only)
      if (isEntityDataCapture && (question_text === 'First Name' || question_text === 'Last Name') && value) {
        if (/\d/.test(value)) {
          return { isValid: false, message: 'Name cannot contain numbers' };
        }
      }

      // Validate Birth Date (Both Entity Data Capture and CIPC)
      if (question_text === 'Birth Date' && value) {
        return validate_date_of_birth_with_slashes(value);
      }

      return { isValid: true, message: '' };
    },
    
    handle_name_input(event, field) {
      // Only apply restrictions for Entity Data Capture service and CIPC services
      const isCIPCService = this.service_name && this.service_name.toLowerCase().includes('cipc');
      const isEntityDataCapture = this.service_name === 'Entity Data Capture';
      
      if (!isEntityDataCapture && !isCIPCService) {
        return;
      }
      
      const question_text = field.question ? field.question.trim() : '';
      
      // Handle First Name and Last Name - only letters, spaces, and hyphens (Entity Data Capture only)
      if (isEntityDataCapture && (question_text === 'First Name' || question_text === 'Last Name')) {
        allow_character(event);
      }
      
      // Handle ID Number - only digits, max 13 (Entity Data Capture only)
      else if (isEntityDataCapture && question_text === 'ID Number') {
        let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
        if (value.length > 13) {
          value = value.substring(0, 13); // Limit to 13 digits
        }
        event.target.value = value;
        field.value = value;
      }
      
      // Handle Company Registration Number - only digits, auto-format with slashes
      else if (question_text === 'Company Registration Number') {
        // Remove all non-digit characters
        let value = event.target.value.replace(/\D/g, '');
        
        // Limit to 12 digits (YYYY + NNNNNN + NN)
        if (value.length > 12) {
          value = value.substring(0, 12);
        }
        
        // Auto-format with slashes: YYYY/NNNNNN/NN
        let formatted = value;
        if (value.length > 4) {
          formatted = value.substring(0, 4) + '/' + value.substring(4);
        }
        if (value.length > 10) {
          formatted = value.substring(0, 4) + '/' + value.substring(4, 10) + '/' + value.substring(10);
        }
        
        event.target.value = formatted;
        field.value = formatted;
      }
      
      // Handle Birth Date - only digits, auto-format with slashes YYYY/MM/DD
      else if (question_text === 'Birth Date') {
        // Remove all non-digit characters
        let value = event.target.value.replace(/\D/g, '');
        
        // Limit to 8 digits (YYYY + MM + DD)
        if (value.length > 8) {
          value = value.substring(0, 8);
        }
        
        // Auto-format with slashes: YYYY/MM/DD
        let formatted = value;
        if (value.length > 4) {
          formatted = value.substring(0, 4) + '/' + value.substring(4);
        }
        if (value.length > 6) {
          formatted = value.substring(0, 4) + '/' + value.substring(4, 6) + '/' + value.substring(6);
        }
        
        event.target.value = formatted;
        field.value = formatted;
      }
    },

    show_error(question_id, is_followup = false, group_index = null) {
      let error_id;
      if (is_followup) {
        error_id = `#error-followup-${question_id}`;
      } else if (group_index !== null) {
        error_id = `#error-duplicate-${group_index}-${question_id}`;
      } else {
        error_id = `#error-${question_id}`;
      }
      $(error_id).removeClass('d-none');
      setTimeout(() => {
        $(error_id).addClass('d-none');
      }, 3000);
    },
    validate_form() {
      let is_valid = true;

      // Reset all error messages first
      [...this.responses, ...this.follow_up_responses].forEach(field => {
        $(`#error-${field.id}`).addClass('d-none');
        $(`#error-followup-${field.id}`).addClass('d-none');
      });

      this.duplicate_fields.forEach((fields, group_index) => {
        fields.forEach(field => {
          $(`#error-duplicate-${group_index}-${field.id}`).addClass('d-none');
        });
      });

      // Helper function to check if a value is considered valid/filled
      const is_value_valid = (field) => {
        const value = field.value;

        if (field.has_date) {
          return typeof value === 'string' && value.trim() !== '';
        }
        if (field.has_file) {
          return (value instanceof File) || (typeof value === 'string' && value.trim() !== '');
        }
        if (field.has_number) {
          return value !== '' && !isNaN(value);
        }
        if (value == null || value === undefined) return false;
        if (Array.isArray(value) || (value && typeof value === 'object' && typeof value.length === 'number')) {
          return value.length > 0;
        }
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return Object.keys(value).length > 0;
        }
        if (typeof value === 'string') {
          return value.trim() !== '';
        }
        if (typeof value === 'number') {
          return !isNaN(value);
        }
        return Boolean(value);
      };

      // Validate main form responses
      this.responses.forEach((field) => {
        if (field.is_required) {
          if (!is_value_valid(field)) {
            if (field.has_number && (field.value === '' || isNaN(field.value))) {
              $(`#error-${field.id}`).text('Please enter a valid number').removeClass('d-none');
            } else {
              this.show_error(field.id);
            }
            setTimeout(() => {
              $(`#error-${field.id}`).addClass('d-none');
            }, 3000);
            is_valid = false;
          } else {
            // Validate specific field types (ID, Company Reg, Names)
            const validation_result = this.validate_field_by_question(field);
            if (!validation_result.isValid) {
              $(`#error-${field.id}`).text(validation_result.message).removeClass('d-none');
              setTimeout(() => {
                $(`#error-${field.id}`).addClass('d-none');
              }, 3000);
              is_valid = false;
            }
          }
        } else if (is_value_valid(field)) {
          // Validate non-required fields if they have values
          const validation_result = this.validate_field_by_question(field);
          if (!validation_result.isValid) {
            $(`#error-${field.id}`).text(validation_result.message).removeClass('d-none');
            setTimeout(() => {
              $(`#error-${field.id}`).addClass('d-none');
            }, 3000);
            is_valid = false;
          }
        }
      });

      // Validate follow-up responses if they exist
      this.follow_up_responses.forEach(follow_up_field => {
        if (follow_up_field.is_required && !is_value_valid(follow_up_field)) {
          if (follow_up_field.has_number && (follow_up_field.value === '' || isNaN(follow_up_field.value))) {
            $(`#error-followup-${follow_up_field.id}`).text('Please enter a valid number').removeClass('d-none');
          } else {
            this.show_error(follow_up_field.id, true);
          }
          setTimeout(() => {
            $(`#error-followup-${follow_up_field.id}`).addClass('d-none');
          }, 3000);
          is_valid = false;
        }
      });

      // Validate duplicate fields if any
      if (this.duplicate_fields.length > 0) {
        this.duplicate_fields.forEach((dupliate_fields, group_index) => {
          dupliate_fields.forEach(dupliate_field => {
            if (dupliate_field.is_required && !is_value_valid(dupliate_field)) {
              if (dupliate_field.has_number && (dupliate_field.value === '' || isNaN(dupliate_field.value))) {
                $(`#error-duplicate-${group_index}-${dupliate_field.id}`).text('Please enter a valid number').removeClass('d-none');
              } else {
                $(`#error-duplicate-${group_index}-${dupliate_field.id}`).removeClass('d-none');
              }
              setTimeout(() => {
                $(`#error-duplicate-${group_index}-${dupliate_field.id}`).addClass('d-none');
              }, 3000);
              is_valid = false;
            }
          });
        });
      }

      // If validation fails, scroll to first error
      if (!is_valid) {
        this.$nextTick(() => {
          const firstError = document.querySelector('.text-danger:not(.d-none)');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }

      return is_valid;
    },
    handle_selection_change(question_id, selected_value) {
      this.get_follow_up_question(question_id, selected_value);
    },
    handle_multiple_selection(event, question_id, option_value) {
      const is_selected = event.target.checked;
      this.get_follow_up_question(question_id, option_value, is_selected);
    },

    handle_followup_multiple_selection(event, question_id, option_value) {
      const is_checked = event.target.checked;
      const follow_up_response = this.follow_up_responses.find(resp => resp.id === question_id);

      if (follow_up_response) {
        if (!Array.isArray(follow_up_response.value)) {
          follow_up_response.value = [];
        }

        if (is_checked) {
          if (!follow_up_response.value.includes(option_value)) {
            follow_up_response.value.push(option_value);
          }
        } else {
          follow_up_response.value = follow_up_response.value.filter(val => val !== option_value);
        }
        this.update_follow_up_response(question_id, follow_up_response.value);
      }
    },

    get_follow_up_question(question_id, option_value, is_selected = true) {
      // Find the question by ID
      const question = this.responses.find(q => q.id === question_id);
      if (!question || !Array.isArray(question.options)) {
        return null;
      }

      // Find the option by ID
      const option = question.options.find(opt => opt.id === option_value || opt.option === option_value);
      if (!option) {
        return null;
      }

      if (question.has_multi_selection) {
        // For multi-selection questions, we only remove follow-up questions 
        // for options that are deselected
        if (!is_selected) {
          // If option is deselected, remove only the follow-up questions related to this specific option
          this.follow_up_questions = this.follow_up_questions.filter(
            q => !(q.parent_id === question_id && q.parent_option === option.id)
          );
          this.follow_up_responses = this.follow_up_responses.filter(
            resp => !(resp.parent_id === question_id && resp.parent_option === option.id)
          );
          return null;
        }
      } else {
        // Remove only the follow-up questions related to this specific question
        this.follow_up_questions = this.follow_up_questions.filter(
          q => q.parent_id !== question_id
        );
        this.follow_up_responses = this.follow_up_responses.filter(
          resp => resp.parent_id !== question_id
        );
      }

      // Check for follow-up questions in the selected option
      if (option.follow_up_question && Array.isArray(option.follow_up_question)) {
        // Process all follow-up questions
        const new_follow_ups = option.follow_up_question.map(follow_up => ({
          parent_id: question_id,
          parent_option: option.id,
          ...follow_up,
        }));

        if (new_follow_ups.length > 0) {
          // Add new follow-up questions while preserving existing ones for other questions
          this.follow_up_questions = [...this.follow_up_questions, ...new_follow_ups];
          // Add all follow-up questions to responses
          this.follow_up_responses.push(...new_follow_ups.map(follow_up => ({
            ...follow_up,
            value: follow_up.has_multi_selection ? [] : '',
            error_message: '',
          })));
        }
      }
    },

    update_follow_up_response(question_id, value) {
      const follow_up = this.follow_up_responses.find(resp => resp.id === question_id);
      if (follow_up) {
        follow_up.value = value;
      }
    },
    add_more_fields() {
      const new_fields = this.responses.map(field => ({
        ...field,
        value: "", // Reset value for the duplicate fields
      }));
      this.duplicate_fields.push(new_fields);
    },
    remove_fields(index) {
      this.duplicate_fields.splice(index, 1);
    },


    apply_entity_data_capture_filter() {
      const config = this.$store.getters.get_entity_data_capture_config;
      if (config && Array.isArray(config.selected_fields) && config.selected_fields.length > 0) {
        this.filter_responses_by_config(config);
        return;
      }

      const TOKEN = localStorage.getItem('token');
      const url = `${API_BASE_URL}/verify_api/service_management_api/get_entity_data_capture_config/?workflow_id=${this.workflow_id}&service_id=${this.service_id}`;

      $.ajax({
        url: url,
        method: 'GET',
        headers: { Authorization: `Token ${TOKEN}` },
        success: (res) => {
          if (res.status === 'success' && res.data) {
            this.$store.commit('set_entity_data_capture_config', res.data);
            this.filter_responses_by_config(res.data);
          } else {
            this.finish_question_load();
          }
        },
        error: () => {
          this.finish_question_load();
        }
      });
    },

    filter_responses_by_config(config) {
      if (config && Array.isArray(config.selected_fields) && config.selected_fields.length > 0) {
        const selectedFieldsLower = config.selected_fields.map(f => f.trim().toLowerCase());
        this.responses = this.responses.filter(
          (field) => selectedFieldsLower.includes(field.question?.trim().toLowerCase())
        );
      }
      this.finish_question_load();
    },

    finish_question_load() {
      this.duplicate_fields = [];
      if (this.form_data.length > 0) {
        this.fetch_customer_responses();
      }
    },

    fetch_custom_service_question() {
      const TOKEN = localStorage.getItem('token');
      const GET_QUESTIONS_API = `${API_BASE_URL}/verify_api/form_management_api/get_questions_for_service_api/${this.service_id}?workflow_id=${this.workflow_id}`;

      $.ajax({
        url: GET_QUESTIONS_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: 'Getting selected service...',
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        },
        success: (response) => {
          Swal.close();
          // Assign questions and service type from the response
          this.service_name = response.service_name;
          this.form_data = response.questions;
          this.service_type = response.service_type; // Add a new data property for service type
          // Map responses for questions
          this.responses = this.form_data.map((field) => ({
            ...field,
            value: field.has_multi_selection ? [] : '', // Initialize with empty value
            error_message: '',
          }));

          if (this.service_name === 'Entity Data Capture') {
            this.apply_entity_data_capture_filter();
            return;
          }

          // Clear duplicate fields upon new fetch
          this.duplicate_fields = [];
          if (this.form_data.length > 0) {
            this.fetch_customer_responses();
          }
        },
        error: () => {
          Swal.fire({
            text: 'An error occurred',
            icon: 'error',
          });
          Swal.close();
        },
      });
    },
    fetch_customer_responses() {
      const TOKEN = localStorage.getItem('token');
      const GET_RESPONSES_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_customer_responses/${this.workflow_id}/${this.service_id}/`;

      $.ajax({
        url: GET_RESPONSES_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          const service_data = response.services && response.services[0];
          if (service_data && service_data.questions && service_data.questions.length > 0) {
            this.process_customer_responses(service_data.questions);
          }
        },
        error: () => {
          Swal.fire({
            text: 'Failed to load saved responses',
            icon: 'error',
          });
        },
      });
    },

    process_customer_responses(questions) {
      this.follow_up_responses = [];
      this.follow_up_questions = [];

      // Process main questions and trigger follow-ups
      this.responses.forEach((resp_field) => {
        const question = questions.find(q => q.question_id === resp_field.id);
        if (question) {
          if (resp_field.has_multi_selection) {
            if (Array.isArray(question.response)) {
              resp_field.value = question.response.map(val => {
                const option = resp_field.options.find(opt => opt.id === val || opt.option === val);
                return option ? option.option : val;
              });
            } else if (typeof question.response === 'string') {
              resp_field.value = question.response.split(',').map(v => v.trim()).filter(Boolean);
            } else {
              resp_field.value = [];
            }
            resp_field.value.forEach(selected_option_value => {
              const option = resp_field.options.find(
                opt => opt.option === selected_option_value || opt.id === selected_option_value
              );
              if (option && option.follow_up_question && option.follow_up_question.length > 0) {
                this.get_follow_up_question(resp_field.id, option.id, true);
              }
            });
          } else if (resp_field.has_file) {
            resp_field.value = question.response || '';
            this.uploaded_file = resp_field.value;
          } else if (resp_field.has_checkbox || resp_field.has_selection) {
            resp_field.value = question.response;
            const option = resp_field.options.find(
              opt => opt.option === question.response || opt.id === question.response
            );
            resp_field.value = option ? option.option : question.response;
            // Trigger follow-up for checkbox/selection
            if (option && option.follow_up_question && option.follow_up_question.length > 0) {
              this.get_follow_up_question(resp_field.id, option.id, true);
            }
          } else {
            resp_field.value = question.response;
          }
        } else {
          if (resp_field.has_multi_selection) resp_field.value = [];
          else resp_field.value = '';
        }
      });

      // Process follow-up questions after main questions are handled
      this.follow_up_responses.forEach((follow_up_resp_field) => {
        const follow_up_question = questions.find(fq => fq.question_id === follow_up_resp_field.id);

        if (follow_up_question) {
          if (follow_up_resp_field.has_multi_selection) {
            if (Array.isArray(follow_up_question.response)) {
              follow_up_resp_field.value = follow_up_question.response.map(val => {
                const option = follow_up_resp_field.options.find(opt => opt.id === val || opt.option === val);
                return option ? option.option : val;
              });
            } else if (typeof follow_up_question.response === 'string') {
              follow_up_resp_field.value = follow_up_question.response.split(',').map(v => v.trim()).filter(Boolean);
            } else {
              follow_up_resp_field.value = [];
            }
            // Handle nested follow-ups
            follow_up_resp_field.value.forEach(selected_option_value => {
              const option = follow_up_resp_field.options.find(
                opt => opt.option === selected_option_value || opt.id === selected_option_value
              );
              if (option && option.follow_up_question && option.follow_up_question.length > 0) {
                this.get_follow_up_question(follow_up_resp_field.id, option.id, true);
              }
            });
          } else if (follow_up_resp_field.has_file) {
            follow_up_resp_field.value = follow_up_question.response || '';
            this.uploaded_file = follow_up_resp_field.value;
          } else if (follow_up_resp_field.has_checkbox || follow_up_resp_field.has_selection) {
            const option = follow_up_resp_field.options.find(
              opt => opt.option === follow_up_question.response || opt.id === follow_up_question.response
            );
            follow_up_resp_field.value = option ? option.option : follow_up_question.response;
            // Trigger nested follow-ups
            if (option && option.follow_up_question && option.follow_up_question.length > 0) {
              this.get_follow_up_question(follow_up_resp_field.id, option.id, true);
            }
          } else {
            follow_up_resp_field.value = follow_up_question.response;
          }
        }
      });
      this.emit_responses();
    },
    emit_responses() {
      // Map all responses to their current values
      this.form_data.forEach(field => {
        const resp = this.responses.find(r => r.id === field.id);
        if (resp) field.value = resp.value;
      });
      this.$emit('update-responses', this.form_data);
    },
    on_file_change(event, question_id, is_follow_up, group_index = null) {
      const FILE = event.target.files[0]; // Get the selected file
      const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

      if (!FILE) return;

      // Check for file size
      if (FILE.type !== 'application/pdf') {
        event.target.value = null; // Clear the file input
        Swal.fire({
          text: 'Please upload a PDF file.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Check for file size
      if (FILE.size > MAX_SIZE) {
        event.target.value = null; // Clear the file input
        Swal.fire({
          text: 'File is too large.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      this.uploaded_file = FILE;

      if (group_index !== null) {
        const field = this.duplicate_fields[group_index].find(f => f.id === question_id);
        if (field) {
          field.value = FILE;
        }
        this.emit_responses();
        return;
      }

      if (is_follow_up) {
        const follow_up = this.follow_up_responses.find(resp => resp.id === question_id);
        if (follow_up) {
          follow_up.value = FILE;
        }
      } else {
        const field = this.responses.find(resp => resp.id === question_id);
        if (field) {
          field.value = FILE;
        }
      }
      this.emit_responses();
    },
    submit_form(skip_validation = false) {
      // Always update values before validation/submission
      this.emit_responses();
      if (!skip_validation) {
        if (!this.validate_form()) {
          this.$nextTick(() => {
            const firstError = document.querySelector('.text-danger:not(.d-none)'); // Selects the first element with text-danger that IS NOT hidden by d-none
            if (firstError) {
              // Scroll the error message into view
              firstError.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          });
          return; // Stop the submission process
        }
      }
      const GET_CLIENT_ID = sessionStorage.getItem('client_admin_id');
      const TOKEN = localStorage.getItem('token');
      const SAVE_RESPONSE_API = `${API_BASE_URL}/verify_api/system_management_api/save_response_api/`;

      // Helper function to process responses including follow-ups
      const process_responses = () => {
        let processed_responses = [];

        // Process main responses first
        this.responses.forEach(field => {
          let value = field.value;
          // For SearchSelect, always use the value (option) not the label
          if (field.has_selection || field.has_checkbox) {
            value = field.value;
          }
          if (field.has_file && value instanceof File) {
            processed_responses.push({
              service_id: this.service_id,
              question_id: field.id,
              value: value.name,
              uploaded_file: value
            });
          } else {
            processed_responses.push({
              service_id: this.service_id,
              question_id: field.id,
              value: value,
            });
          }

          // Find and add any follow-up responses for this question
          const follow_ups = this.follow_up_responses.filter(
            follow_up => follow_up.parent_id === field.id
          );

          if (follow_ups.length > 0) {
            follow_ups.forEach(follow_up => {
              let follow_value = follow_up.value;
              if (follow_up.has_file && follow_value instanceof File) {
                processed_responses.push({
                  service_id: this.service_id,
                  question_id: follow_up.id,
                  value: follow_value.name,
                  uploaded_file: follow_value
                });
              } else {
                processed_responses.push({
                  service_id: this.service_id,
                  question_id: follow_up.id,
                  value: follow_value,
                });
              }
            });
          }
        });

        // Process duplicate fields if any
        this.duplicate_fields.forEach((fields, index) => {
          fields.forEach(field => {
            let value = field.value;
            if (field.has_file && value instanceof File) {
              processed_responses.push({
                service_id: this.service_id,
                question_id: field.id,
                value: value.name,
                uploaded_file: value,
                is_duplicate: true,
                duplicate_index: index
              });
            } else {
              processed_responses.push({
                service_id: this.service_id,
                question_id: field.id,
                value: value,
                is_duplicate: true,
                duplicate_index: index
              });
            }
          });
        });
        return processed_responses;
      };

      const all_responses = process_responses();

      // Check if any response includes a file
      const has_file = all_responses.some(response => {
        const field = [...this.responses, ...this.follow_up_responses].find(
          f => f.id === response.question_id
        );
        return field && field.has_file;
      });

      let data_to_send;
      if (has_file) {
        // If there's a file, create a FormData instance
        const form_data = new FormData();
        form_data.append('client_admin_id', GET_CLIENT_ID);
        form_data.append('workflow_id', this.workflow_id);
        form_data.append('is_incomplete', this.is_incomplete);

        // Append responses, handling files appropriately
        all_responses.forEach((response, index) => {
          form_data.append(`responses[${index}][question_id]`, response.question_id);
          form_data.append(`responses[${index}][value]`, response.value || '');

          if (response.uploaded_file) {
            form_data.append(`responses[${index}][uploaded_file]`, response.uploaded_file);
          }
          if (response.is_duplicate) {
            form_data.append(`responses[${index}][is_duplicate]`, true);
            form_data.append(`responses[${index}][duplicate_index]`, response.duplicate_index);
          }
          if (response.service_id) {
            form_data.append(`responses[${index}][service_id]`, response.service_id);
          }
        });

        form_data.append('on_behalf_application', this.$route.name === 'behalf_application' ? true : false);

        data_to_send = form_data;
      } else {
        // If no files, send as JSON
        data_to_send = JSON.stringify({
          client_id: GET_CLIENT_ID,
          workflow_id: this.workflow_id,
          responses: all_responses,
          is_incomplete: this.is_incomplete,
          on_behalf_application: this.$route.name === 'behalf_application' ? true : false,
        });
      }

      // Determine content type based on the presence of files
      const content_type = has_file ? false : 'application/json';

      $.ajax({
        url: SAVE_RESPONSE_API,
        method: 'POST',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        data: data_to_send,
        processData: !has_file,
        contentType: content_type,
        beforeSend: () => {
          Swal.fire({
            text: 'Loading ...',
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        },
        success: () => {
          Swal.close();
          if (!this.is_incomplete) {
            this.fetch_current_service();
          }
        },
        error: () => {
          Swal.close();
        }
      });
    },
    go_back() {
      this.is_incomplete = true;
      this.submit_form(true);
      this.fetch_previous_service();
      this.follow_up_responses = [];
      this.follow_up_questions = [];
    },
    save_and_skip() {
      this.is_incomplete = true;
      this.submit_form(true);
      this.fetch_next_service();
      this.follow_up_responses = [];
      this.follow_up_questions = [];
    },
    continue_to_next() {
      this.is_incomplete = false;
      this.submit_form(false);
    }
  },

  mounted() {
    // Simulating API call to get form data
    if (!validateToken(this.$router)) {
      return;
    }
    setTimeout(() => {
      this.is_form_data_ready = true;
    }, 1000);
    this.file_input = this.$refs.file_input;
    this.follow_up_file_input = this.$refs.follow_up_file_input;
  },
};
